import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentUser, useLogout } from "@/hooks/useAuth";
import { useConfigStore } from "@/stores/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProfileNarrative } from "@/components/profile/ProfileNarrative";
import { ProfileEditForm } from "@/components/profile/ProfileEditForm";
import { referenceLabel } from "@/lib/reference";
import { isInstitutionOrgDerived } from "@/components/profile/institutionOrigin";

import { cn } from "@/lib/utils";

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const logout = useLogout();
  const { config } = useConfigStore();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleLogout = async () => {
    await logout.mutateAsync();
    void navigate("/login");
  };

  // Same gate as ProfileEditForm's read-only institution row (see
  // isInstitutionOrgDerived): organisationRef alone is deployment-wide, true
  // for every caller once one org root exists — it can't answer "does THIS
  // user belong here". A real location membership can.
  const institutionOrgDerived = isInstitutionOrgDerived(
    user?.organisationRef,
    !!user?.locationMemberships?.length,
  );
  const institutionValue = institutionOrgDerived
    ? referenceLabel(user?.organisationRef) || user?.institution
    : user?.institution;

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200/50">
        <div className="px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate("/home")}
            aria-label={t("common.back")}
            className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>
          <h1 className="text-base font-medium text-neutral-900">
            {t("profile.title")}
          </h1>
          {user && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="ml-auto text-sm font-medium text-primary hover:text-primary-600 transition-colors active:scale-95"
            >
              {t("common.edit")}
            </button>
          )}
        </div>
      </header>

      {/* Profile Content */}
      <main className="flex-1 px-4 py-6 space-y-6">
        {/* Avatar */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-2xl font-semibold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          </div>
        </div>

        {isEditing && user ? (
          <ProfileEditForm user={user} onDone={() => setIsEditing(false)} />
        ) : (
          <>
            {/* Her own summary of her practice. Nothing generates it — the
                AI-written summary and its regenerate button went with the route
                behind them, which the platform no longer serves. */}
            <ProfileNarrative narrative={user?.narrative ?? null} />

            {/* Info Fields (read-only; tap Edit to change) */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-500">
                  {t("auth.email")}
                </label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-500">
                  {t("profile.name")}
                </label>
                <Input
                  value={user?.name || ""}
                  disabled
                  className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-500">
                  {t("profile.role")}
                </label>
                <Input
                  value={referenceLabel(user?.roleRef) || user?.role || ""}
                  disabled
                  className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                />
              </div>

              {/* Institution — the deployment's live org name wins over the
                  free-text snapshot taken at onboarding ONLY for a user who
                  actually belongs to this org (institutionOrgDerived, above);
                  a non-member's own recorded institution stands, and a
                  non-member with no recorded value omits this row entirely —
                  never a fabricated org name. */}
              {institutionValue && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-500">
                    {t("profile.institution")}
                  </label>
                  <Input
                    value={institutionValue}
                    disabled
                    className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                  />
                </div>
              )}

              {/* Ward/unit — the SELF-ASSIGNED fact only (locationRef, or its
                  free-text display mirror `unit`). ALWAYS rendered, with an
                  explicit empty state when unset — never silently disappears.
                  Admin-granted ward memberships are a DIFFERENT fact and are
                  never poured into this row as a substitute; they get their
                  own row below. */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-500">
                  {t("profile.unit")}
                </label>
                <Input
                  value={referenceLabel(user?.locationRef) || user?.unit || t("profile.unitEmpty")}
                  disabled
                  className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                />
              </div>

              {/* Ward memberships — admin-granted (FGA), independent of the
                  self-assigned unit above. Rendered only when present, same
                  convention as ProfileEditForm's own read-only memberships
                  list — omitted rather than shown with a placeholder empty
                  state, since most nurses carry none beyond their own unit. */}
              {!!user?.locationMemberships?.length && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-500">
                    {t("profile.wardMemberships")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {user.locationMemberships.map((m) => (
                      <span
                        key={m.location_id}
                        className="px-3 py-2 rounded-full text-sm bg-neutral-100 border border-neutral-200 text-neutral-600"
                      >
                        {m.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-500">
                  {t("profile.twinName")}
                </label>
                <Input
                  value={user?.twinName || ""}
                  disabled
                  className="h-12 text-base bg-neutral-100 border-neutral-200 text-neutral-600"
                />
              </div>
            </div>

            {/* Logout */}
            <div className="mt-10 pt-6 border-t border-neutral-200">
              {showLogoutConfirm ? (
                <div className="p-4 rounded-xl bg-neutral-100 space-y-3">
                  <p className="text-sm text-neutral-700">
                    {t("profile.logoutConfirm")}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowLogoutConfirm(false)}
                      className="flex-1 h-10"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleLogout}
                      disabled={logout.isPending}
                      className="flex-1 h-10 bg-error hover:bg-error/90"
                    >
                      {logout.isPending ? t("common.loading") : t("auth.logout")}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className={cn(
                    "w-full py-3 text-center text-error font-medium",
                    "rounded-xl hover:bg-error/5 transition-colors",
                  )}
                >
                  {t("profile.logout")}
                </button>
              )}
            </div>
          </>
        )}

        {/* Version Footer */}
        <div className="mt-8 pt-4 border-t border-neutral-200 space-y-1">
          <p className="text-xs text-neutral-400 text-center">
            Config: {config?.version || "unknown"}
          </p>
        </div>
      </main>

    </div>
  );
}
